import React, { useId, useState } from "react";

interface TooltipTextProps {
  children: React.ReactNode;
  tooltip: string;
  position?: "top" | "bottom" | "left" | "right";
  className?: string;
}

export function TooltipText({
  children,
  tooltip,
  position = "top",
  className = "",
}: TooltipTextProps) {
  const [isVisible, setIsVisible] = useState(false);
  const tooltipId = useId();

  const show = () => setIsVisible(true);
  const hide = () => setIsVisible(false);

  const getTooltipPosition = () => {
    switch (position) {
      case "bottom":
        return "top-full left-1/2 transform -translate-x-1/2 mt-2";
      case "left":
        return "right-full top-1/2 transform -translate-y-1/2 mr-2";
      case "right":
        return "left-full top-1/2 transform -translate-y-1/2 ml-2";
      default: // top
        return "bottom-full left-1/2 transform -translate-x-1/2 mb-2";
    }
  };

  const getArrowPosition = () => {
    switch (position) {
      case "bottom":
        return "bottom-full left-1/2 transform -translate-x-1/2 border-l-transparent border-r-transparent border-b-gray-800";
      case "left":
        return "left-full top-1/2 transform -translate-y-1/2 border-t-transparent border-b-transparent border-l-gray-800";
      case "right":
        return "right-full top-1/2 transform -translate-y-1/2 border-t-transparent border-b-transparent border-r-gray-800";
      default: // top
        return "top-full left-1/2 transform -translate-x-1/2 border-l-transparent border-r-transparent border-t-gray-800";
    }
  };

  return (
    <span
      className={`relative inline-block cursor-help ${className}`}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      tabIndex={0}
      aria-describedby={isVisible ? tooltipId : undefined}
    >
      <span className="decoration-soluud-primary hover:decoration-soluud-primary-hover underline decoration-dotted transition-colors">
        {children}
      </span>

      {isVisible && (
        <div
          id={tooltipId}
          role="tooltip"
          className={`absolute z-10 ${getTooltipPosition()}`}
        >
          <div className="w-max max-w-xs rounded-lg bg-gray-800 px-3 py-2 text-sm break-words text-white shadow-lg">
            {tooltip}
          </div>
          <div
            className={`absolute h-0 w-0 border-4 ${getArrowPosition()}`}
          ></div>
        </div>
      )}
    </span>
  );
}
